import React from "react";
import BaseTabComponent from "../UnderwritingMultiTenant/tabs-components/BaseTabComponent";
import {
    offeringSummary as offeringSummaryShape,
    incomeAndExpenses as incomeAndExpensesShape,
    rentRoll as rentRollShape,
    getOfferingSummaryData,
    getIncomeAndExpenses,
    getRentRoll,
} from "../PrintMultiTenant/tables";
import {addCommasToNumber} from "../../utils";
import dollarFormat from "../../dollar-format";

import Cover from "./pages/Cover";
import BackCover from "./pages/BackCover";
import OfferingSummary from "./pages/OfferingSummary";
import OfferingSummarySingle from "./pages/OfferingSummarySingle";
import OfferingSummaryMultiFamily from "./pages/OfferingSummaryMultiFamily";
import UnitSummary from "./pages/UnitSummary";
import RentRoll from "./pages/RentRoll";
import ValueAnalysis from "./pages/ValueAnalysis";
import GlobalReach from "./pages/GlobalReach";
import CapitalFlows from "./pages/CapitalFlows";
import ProprietaryDatabase from "./pages/ProprietaryDatabase";
import TexasAdvantage from "./pages/TexasAdvantage";
import AssetClassCoverage from "./pages/AssetClassCoverage";
import Team, {paginateTeamSections} from "./pages/Team";

import {resolveValueAnalysisCopy} from "./bov-boilerplate";
import {collectPreloadUrls, isMultiFamily, isSingleTenant, subProductLabel} from "./bov-templates";
import * as singleTenant from "./bov-data-single";
import * as multiFamily from "./bov-data-multifamily";
import {withBovStats} from "./bov-stats";
import {loadBovTeam} from "./bov-team";
import {resolveStaticMapUrl} from "./bov-map";
import {loadBovClosings} from "./bov-closings";
import {loadBovCounts} from "./bov-counts";

const cloneDeep = require('lodash.clonedeep');

// Map panel size on page 02 (y 388..773 in the template).
const MAP_WIDTH = 480;
const MAP_HEIGHT = 385;
const RENT_ROLL_ROWS_PER_PAGE = 12;
const PAGE_STYLE_ID = 'bov-print-page-size';

/**
 * The marketing tail, in template order - the same six pages in both decks, every one of
 * them markup. Those with statistics behind them print a dash wherever a service is not yet
 * served; Global Reach has none and prints template content throughout.
 *
 * Global Reach was a flat image until 2026-09-12 and sat here as a `null` whose asset was
 * looked up per template. Nothing is keyed by page number any more, which is what the
 * `templatePage` below is still for: the tail starts at page 05 on the multi-tenant deck
 * (cover, offering summary, rent roll, value analysis) and at 04 on the single-tenant one,
 * which has no rent roll.
 */
const MARKETING_TAIL = [
    CapitalFlows,
    GlobalReach,
    ProprietaryDatabase,
    TexasAdvantage,
    AssetClassCoverage,
    Team,
];

export default class PrintBOV extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            // The Team roster, loaded on mount.
            teamSections: null,
            teamError: '',
            mapImageUrl: '',
            closings: null,
            counts: null,
            data: {
                offeringSummary: offeringSummaryShape,
                incomeAndExpenses: incomeAndExpensesShape,
                rentRoll: rentRollShape,
            },
            // Page 02's three blocks on the single-tenant deck. Null until the first sync.
            pageTwo: null,
        };
    }

    /** Which of the two underwriting shapes this deck is being fed. */
    get isSingle() {
        return isSingleTenant(this.props.template);
    }

    /**
     * Multi-Family: fed by the Multi-Family underwriting (`multiFamily` = {inputs, calc})
     * rather than the multi-tenant `tabs`, with its own pages 02 and 03.
     */
    get isMultiFamily() {
        return isMultiFamily(this.props.template);
    }

    componentDidMount() {
        PrintBOV.applyLandscapePageSize();
        PrintBOV.preload(collectPreloadUrls(this.props.template));
        this.loadTeam();
        this.loadClosings();
        this.loadCounts();

        // The single-tenant underwriting hands over a populated `tables` immediately,
        // whereas the multi-tenant `tabs` fill in later and are picked up in
        // componentWillReceiveProps. Only the multi-tenant page 02 carries a map.
        if (this.isSingle) this.syncSingleTenant(this.props);
        else this.resolveMap(this.props);
    }

    /**
     * The Team page's roster. A failure leaves the section empty rather than breaking
     * the rest of the deck - no other page depends on it.
     */
    loadTeam() {
        loadBovTeam()
            .then(teamSections => {
                if (!this.unmounted) this.setState({teamSections});
            })
            .catch(err => {
                // The message is kept and printed on the page. fetchData also logs it,
                // but a failure that is only visible in the console reads on the page as
                // an empty roster, which is worse than an ugly one.
                const message = (err && err.message) || 'Unknown error';
                if (!this.unmounted) this.setState({teamSections: [], teamError: message});
            });
    }

    /**
     * Closed-transaction totals for Capital Flows and The Texas Advantage. As with the
     * roster, a failure leaves those figures as dashes rather than taking the deck down.
     */
    loadClosings() {
        loadBovClosings()
            .then(closings => {
                if (!this.unmounted) this.setState({closings});
            })
            .catch(() => { /* figures stay as dashes */ });
    }

    /** The Proprietary Database page's counts. */
    loadCounts() {
        loadBovCounts()
            .then(counts => {
                if (!this.unmounted) this.setState({counts});
            })
            .catch(() => { /* cards stay as dashes */ });
    }

    componentWillUnmount() {
        this.unmounted = true;
        PrintBOV.removeLandscapePageSize();
    }

    /**
     * The map panel. Uses the property's coordinates when it has them and geocodes its
     * address when it does not, so this has to re-run once the underwriting tabs have
     * populated - at mount the address rows are still blank. Keyed so the lookup is not
     * repeated on every prop change.
     */
    resolveMap(props) {
        const property = PrintBOV.mapPropertyFrom(props);
        if (!property) return;
        const mapProps = props.propertyMapProps || {};
        const key = [mapProps.latitude, mapProps.longitude,
            property.address, property.city, property.state, property.zip].join('|');
        if (key === this.mapKey) return;
        this.mapKey = key;

        resolveStaticMapUrl(mapProps, property, MAP_WIDTH, MAP_HEIGHT)
            .then(mapImageUrl => {
                if (!this.unmounted && mapImageUrl) this.setState({mapImageUrl});
            })
            .catch(() => { /* panel prints empty */ });
    }

    componentWillReceiveProps(nextProps) {
        if (isSingleTenant(nextProps.template)) {
            this.syncSingleTenant(nextProps);
            return;
        }

        this.resolveMap(nextProps);
        // The Multi-Family pages read their data straight from `multiFamily` at render time.
        if (isMultiFamily(nextProps.template)) return;
        if (!nextProps.tabs) return;
        const data = cloneDeep(this.state.data);
        const precomputed = BaseTabComponent.precomputeHeavilyUsedValues(nextProps.tabs);

        data.offeringSummary.rows = getOfferingSummaryData(nextProps.tabs, precomputed);
        data.incomeAndExpenses = getIncomeAndExpenses(nextProps.tabs, precomputed);
        data.rentRoll = getRentRoll(nextProps.tabs, precomputed);

        this.setState({data});
    }

    /**
     * Page 02's blocks on the single-tenant deck. Rebuilt from the underwriting tables on
     * every change, as the existing single-tenant deck does - the figures are derived
     * through getComputedValue chains rather than stored, so there is nothing to diff
     * against cheaply.
     */
    syncSingleTenant(props) {
        if (!props.tables) return;
        this.setState({pageTwo: singleTenant.readPageTwo(props.tables, props.template.productType)});
    }

    /**
     * Falls back to reading the tables directly, which covers the first render: unlike the
     * multi-tenant tabs, `tables` arrives populated, so the deck has everything it needs
     * before componentDidMount has run to fill the cache.
     */
    readPageTwo() {
        return this.state.pageTwo
            || singleTenant.readPageTwo(this.props.tables, this.props.template.productType);
    }

    /**
     * The previous deck is portrait and this one is landscape, and `@page` cannot be
     * scoped to a component. The rule is therefore injected while this component is
     * mounted and taken back out on unmount, so the two templates do not fight.
     */
    static applyLandscapePageSize() {
        if (typeof document === 'undefined') return;
        if (document.getElementById(PAGE_STYLE_ID)) return;
        const style = document.createElement('style');
        style.id = PAGE_STYLE_ID;
        style.textContent = '@media print { @page { size: 11in 8.5in; margin: 0; } }';
        document.head.appendChild(style);
    }

    static removeLandscapePageSize() {
        if (typeof document === 'undefined') return;
        const style = document.getElementById(PAGE_STYLE_ID);
        if (style && style.parentNode) style.parentNode.removeChild(style);
    }

    static preload(urls) {
        if (typeof document === 'undefined') return;
        urls.forEach(href => {
            if (!href) return;
            if (document.querySelector(`link[rel="preload"][data-bov-img="1"][href="${href}"]`)) return;
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = href;
            link.setAttribute('data-bov-img', '1');
            document.head.appendChild(link);
        });
    }

    readProperty() {
        if (this.isSingle) return singleTenant.readProperty(this.props.tables);
        if (this.isMultiFamily) return multiFamily.readProperty(this.props.multiFamily);
        return PrintBOV.readPropertyFrom(this.props.tabs);
    }

    /** The address the map panel geocodes, once the underwriting has loaded. */
    static mapPropertyFrom(props) {
        if (isMultiFamily(props.template)) return props.multiFamily ? multiFamily.readProperty(props.multiFamily) : null;
        return props.tabs ? PrintBOV.readPropertyFrom(props.tabs) : null;
    }

    static readPropertyFrom(tabs) {
        const general = tabs['property-information-tab'].tables['general-information'].rows;
        return {
            name: general['property-name'].value,
            address: general['address'].value,
            city: general['city'].value,
            state: general['state'].value,
            zip: general['zip'].value,
        };
    }

    readPricing(precomputed) {
        if (this.isSingle) return singleTenant.readPricing(this.props.tables);
        if (this.isMultiFamily) return multiFamily.readPricing(this.props.multiFamily);

        const {tabs} = this.props;
        const isProforma = tabs['pricing-tab'].tables['pricing-analysis']
            .rows['proforma-checkbox'].valueCells[1].value == true;
        const matrix = tabs['pricing-tab'].tables[
            isProforma ? 'pricing-sensitivity-matrix-pro' : 'pricing-sensitivity-matrix'
        ];

        // Cell order in the matrix is aggressive, anticipated, conservative.
        const read = (rowKey, cellIndex, field) =>
            matrix.rows[rowKey].valueCells[cellIndex].getComputedValue(tabs, precomputed)[field] || '0';

        // PSF is only printed on the office and industrial decks - see BellCurve - but it is
        // read either way: the matrix supplies it for every product, and leaving the figure out
        // of the band would put the decision about what prints into the data read.
        const band = cellIndex => ({
            price: `$${addCommasToNumber(read('value', cellIndex, 'internalValue'))}`,
            psf: read('sale-price-psf', cellIndex, 'displayValue'),
            capRate: read('cap-rate', cellIndex, 'displayValue'),
        });

        return {
            aggressive: band(1),
            anticipated: band(2),
            conservative: band(3),
        };
    }

    readFacts(precomputed) {
        if (this.isSingle) return singleTenant.readFacts(this.props.tables);
        // Multi-Family prints four facts, Price Per Unit and Number of Units in place of Lot
        // Size, as its reference does.
        if (this.isMultiFamily) return multiFamily.readFacts(this.props.multiFamily);

        const {tabs} = this.props;
        const offering = tabs['marketing-tab'].tables['offering-summary'].rows;

        const yearBuilt = offering['year-built'].getComputedValue(tabs, precomputed).internalValue || '-';
        const lotSize = offering['lot-size'].getComputedValue(tabs, precomputed).internalValue || '0';
        const noiValue = tabs['pricing-tab'].tables['net-operating-income']
            .rows['net-operating-income'].valueCells[2].getComputedValue(tabs, precomputed).internalValue;

        // No LEASE TYPE: it is a single-tenant concept. A multi-tenant property has a
        // lease type per tenant rather than one for the property, which is why it was
        // dropped from the rent roll on page 03 as well.
        //
        // Both multi-tenant reference templates do print a fourth LEASE TYPE fact here -
        // "NNN" on Houston retail, "???" on Houston office. Departing from them is
        // deliberate and confirmed (2026-08-18): a multi-tenant BOV does not need a lease
        // type. Do not add the fourth fact back.
        return [
            {value: yearBuilt, label: 'YEAR BUILT'},
            {value: noiValue ? dollarFormat(noiValue) : '-', label: 'NET OPERATING INCOME'},
            {value: `${lotSize} Acres`, label: 'LOT SIZE'},
        ];
    }

    chunkRentRoll() {
        const {rentRoll} = this.state.data;
        const rows = rentRoll.rows || [];
        if (!rows.length) return [rentRoll];

        const chunks = [];
        for (let i = 0; i < rows.length; i += RENT_ROLL_ROWS_PER_PAGE) {
            chunks.push({...rentRoll, rows: rows.slice(i, i + RENT_ROLL_ROWS_PER_PAGE)});
        }
        return chunks;
    }

    /**
     * The multi-tenant deck is 11 pages plus however many extra pages the rent roll and
     * the team roster spill onto; the single-tenant deck is 10 plus the roster's spill,
     * having no rent roll. With the current roster the team takes two pages, so both
     * decks print one more than they used to. Both counts include
     * the back cover, which the reference PDFs do not have. Either way the
     * sequence is built first and page numbers are assigned from it, so the printed
     * numbering always matches the pages actually produced.
     */
    buildSequence() {
        const sequence = [
            {kind: 'cover'},
            {kind: 'offering-summary'},
        ];

        if (this.isMultiFamily) {
            // The unit summary takes the rent roll's place, so the marketing tail still
            // starts at template page 05.
            sequence.push({kind: 'unit-summary'});
        } else if (!this.isSingle) {
            const rentRollChunks = this.chunkRentRoll();
            rentRollChunks.forEach((chunk, index) => {
                sequence.push({
                    kind: 'rent-roll',
                    chunk,
                    isLastChunk: index === rentRollChunks.length - 1,
                });
            });
        }

        sequence.push({kind: 'value-analysis'});

        // Template page numbers, not deck positions: the static asset is keyed by the
        // number the page carries in its own template.
        const firstMarketingPage = this.isSingle ? 4 : 5;
        MARKETING_TAIL.forEach((Component, index) => {
            const templatePage = firstMarketingPage + index;

            // The team roster no longer fits one page, so it takes as many as it needs.
            // It is last in the tail, so the pages it adds shift nothing but the back
            // cover - no other page's templatePage key moves.
            if (Component === Team) {
                paginateTeamSections(this.state.teamSections || []).forEach(chunk => {
                    sequence.push({
                        kind: 'template-page',
                        templatePage,
                        Component,
                        teamChunk: chunk,
                        continued: sequence.some(p => p.Component === Team),
                    });
                });
                return;
            }

            sequence.push({kind: 'template-page', templatePage, Component});
        });

        // The back cover closes the deck. It is not in the reference PDFs - those end on the
        // team page - so it is appended here rather than being part of MARKETING_TAIL, and it
        // takes the last page number, which is why every deck prints one page more than its
        // reference does.
        sequence.push({kind: 'back-cover'});

        return sequence;
    }

    render() {
        const {tabs, tables, template, agents, proposalData} = this.props;
        if (!template) return null;
        // Each deck is fed by its own underwriting component, so only one of the three
        // shapes is ever present.
        const single = isSingleTenant(template);
        const mf = this.isMultiFamily ? this.props.multiFamily : null;
        if (single ? !tables : (this.isMultiFamily ? !mf : !tabs)) return null;

        const precomputed = single || mf ? null : BaseTabComponent.precomputeHeavilyUsedValues(tabs);
        const property = this.readProperty();
        const pricing = this.readPricing(precomputed);
        const facts = this.readFacts(precomputed);
        // Dashes stand in for every statistic that has no service behind it yet.
        const stats = withBovStats({
            ...this.props.stats,
            ...(this.state.closings || {}),
            ...(this.state.counts || {}),
            teamSections: this.state.teamSections || (this.props.stats || {}).teamSections,
        });
        const copy = resolveValueAnalysisCopy(template.productType, proposalData || {});
        // The Value Analysis Track Record bullet quotes the same closed count Capital Flows
        // prints. Just the figure - it used to link to the closed-transactions list, and the
        // business asked for the number alone.
        const transactions = stats.totalPropertiesSold;
        const teamStatus = this.state.teamError
            ? 'error'
            : (this.state.teamSections === null ? 'loading' : 'ready');
        const teamError = this.state.teamError;

        const sequence = this.buildSequence();
        const totalPages = sequence.length;
        // "SINGLE TENANT" / "MULTI TENANT", printed in the eyebrow on pages 02..04.
        const subProduct = subProductLabel(template);

        return (
            <div className='PrintBOV'>
                {sequence.map((page, index) => {
                    const pageNo = index + 1;
                    const key = `${page.kind}-${index}`;

                    switch (page.kind) {
                        case 'cover':
                            // No pageNo, and no agents: the cover's foot bar names the
                            // property rather than carrying a contact strip. It still
                            // occupies a page, so it stays in the sequence and in totalPages.
                            return (
                                <Cover
                                    key={key}
                                    template={template}
                                    property={property}
                                />
                            );

                        case 'back-cover':
                            // No pageNo: the back cover prints no page number. It still
                            // occupies a page, so it stays in the sequence and in totalPages.
                            return (
                                <BackCover
                                    key={key}
                                    template={template}
                                    agents={agents}
                                />
                            );

                        case 'offering-summary':
                            if (this.isMultiFamily) {
                                return (
                                    <OfferingSummaryMultiFamily
                                        key={key}
                                        property={property}
                                        subProduct={subProduct}
                                        offeringSummary={multiFamily.readOfferingSummary(mf)}
                                        incomeAndExpenses={multiFamily.readIncomeAndExpenses(mf)}
                                        agents={agents}
                                        mapImageUrl={this.state.mapImageUrl}
                                        pageNo={pageNo}
                                        totalPages={totalPages}
                                    />
                                );
                            }
                            if (single) {
                                const pageTwo = this.readPageTwo();
                                return (
                                    <OfferingSummarySingle
                                        key={key}
                                        property={property}
                                        subProduct={subProduct}
                                        offeringSummary={pageTwo.offeringSummary}
                                        leaseSummary={pageTwo.leaseSummary}
                                        annualizedOperatingData={pageTwo.annualizedOperatingData}
                                        propertyImageUrl={
                                            singleTenant.resolvePropertyImageUrl(this.props.propertyImage)
                                        }
                                        agents={agents}
                                        pageNo={pageNo}
                                        totalPages={totalPages}
                                    />
                                );
                            }
                            return (
                                <OfferingSummary
                                    key={key}
                                    property={property}
                                    subProduct={subProduct}
                                    offeringSummary={this.state.data.offeringSummary}
                                    incomeAndExpenses={this.state.data.incomeAndExpenses}
                                    agents={agents}
                                    mapImageUrl={this.state.mapImageUrl}
                                    pageNo={pageNo}
                                    totalPages={totalPages}
                                />
                            );

                        case 'unit-summary':
                            return (
                                <UnitSummary
                                    key={key}
                                    summary={multiFamily.readUnitSummary(mf)}
                                    subProduct={subProduct}
                                    propertyImageUrls={(this.props.propertyImages || [])
                                        .map(image => String(image || '').trim())
                                        .filter(Boolean)
                                        .map(singleTenant.resolvePropertyImageUrl)}
                                    pageNo={pageNo}
                                    totalPages={totalPages}
                                />
                            );

                        case 'rent-roll':
                            return (
                                <RentRoll
                                    key={key}
                                    rentRoll={page.chunk}
                                    subProduct={subProduct}
                                    isLastChunk={page.isLastChunk}
                                    pageNo={pageNo}
                                    totalPages={totalPages}
                                />
                            );

                        case 'value-analysis':
                            return (
                                <ValueAnalysis
                                    key={key}
                                    pricing={pricing}
                                    productType={template.productType}
                                    facts={facts}
                                    copy={copy}
                                    transactions={transactions}
                                    subProduct={subProduct}
                                    pageNo={pageNo}
                                    totalPages={totalPages}
                                />
                            );

                        case 'template-page': {
                            const MarketingPage = page.Component;
                            return (
                                <MarketingPage
                                    key={key}
                                    template={template}
                                    stats={stats}
                                    subProduct={subProduct}
                                    teamStatus={teamStatus}
                                    teamError={teamError}
                                    teamChunk={page.teamChunk}
                                    continued={page.continued}
                                    pageNo={pageNo}
                                    totalPages={totalPages}
                                />
                            );
                        }

                        default:
                            return null;
                    }
                })}
            </div>
        );
    }
}
